import * as yup from 'yup';
import { yupDateTransformer } from '../../utilities/dayjs-utilities';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'utilities/constants';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';

const validateEmails = (value: string | undefined): boolean => {
  if (!value) return true;

  const emailList = value.split(',');

  if (emailList.length > 3) return false;

  return emailList.every((email) => email && yup.string().email().isValidSync(email.trim()));
};

const validateLocations = (value: string | undefined): boolean => {
  if (!value) return true;

  const locationList = value.split(',');

  return locationList.every((location) => location && location.trim().match(Regex.LOCATION_NUMBER));
};

export function createProcessedDatesValidationSchema({
  minDate,
  maxDate,
}: {
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  return yup
    .object()
    .shape({
      processedDateFrom: yup
        .date()
        .nullable()
        .transform(yupDateTransformer)
        .required(Validation.DATE_FROM_REQUIRED)
        .max(maxDate.format(DATE_FORMAT), Validation.DATE_BEFORE_TODAY)
        .test('date-range', `Must be on or after ${minDate.format(DATE_FORMAT)}.`, function (value) {
          return !value || dayjs(value).isSame(minDate) || dayjs(value).isAfter(minDate);
        }),
      processedDateTo: yup
        .date()
        .nullable()
        .transform(yupDateTransformer)
        .required(Validation.DATE_TO_REQUIRED)
        .max(maxDate, Validation.DATE_BEFORE_TODAY)
        .min(yup.ref('processedDateFrom'), Validation.DATE_TO_AFTER_FROM)
        .test('date-range', `Must be on or after ${minDate.format(DATE_FORMAT)}.`, function (value) {
          return !value || dayjs(value).isSame(minDate) || dayjs(value).isAfter(minDate);
        }),
    })
    .defined();
}

export const renewalPeriodValidationSchema = yup.object().shape({
  cardRenewalPeriod: yup.string().required(Validation.CARD_RENEWAL_PERIOD_REQUIRED),
});

export const reportingDayOfWeekValidationSchema = yup.object().shape({
  reportingDayOfWeek: yup.string().required(Validation.REPORTING_DAY_OF_WEEK_REQUIRED),
});

export const validationSchema = yup.object().shape({
  cardNumber: yup
    .string()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .test(
      'valid-card-numbers',
      'Invalid card number(s)', // Fallback error message (rarely used here)
      function (value) {
        if (!value) return true; // Allow null or empty values

        const cardNumbers = value.split(',').map((num) => num.trim());
        const errors: string[] = [];

        cardNumbers.forEach((cardNumber, index) => {
          if (cardNumber.length !== 16) {
            errors.push(`Card ${index + 1} must be exactly 16 digits.`);
          }
          if (!Regex.ONLY_NUMBERS.test(cardNumber)) {
            errors.push(`Card ${index + 1} must contain only numeric digits.`);
          }
        });

        if (errors.length > 0) {
          // Dynamically return the error message(s)
          return this.createError({
            path: this.path,
            message: errors.join(' '),
          });
        }

        return true; // Validation passes
      }
    ),
  minNumberOfCarWashes: yup
    .number()
    .typeError(Validation.ONLY_NUMBERS)
    .integer(Validation.ONLY_NUMBERS)
    .max(999, Validation.MAX_DIGITS_THREE)
    .nullable()
    .transform((value, originalValue) => (originalValue.trim() === '' ? null : value)),
  minNumberOfFills: yup
    .number()
    .typeError(Validation.ONLY_NUMBERS)
    .integer(Validation.ONLY_NUMBERS)
    .max(999, Validation.MAX_DIGITS_THREE)
    .nullable()
    .transform((value, originalValue) => (originalValue.trim() === '' ? null : value)),
  minTransactionVolume: yup
    .number()
    .typeError(Validation.ONLY_NUMBERS)
    .integer(Validation.ONLY_NUMBERS)
    .max(999999, Validation.MAX_DIGITS_SIX)
    .nullable()
    .transform((value, originalValue) => (originalValue.trim() === '' ? null : value)),
  minTransactionAmount: yup
    .number()
    .typeError(Validation.ONLY_NUMBERS)
    .integer(Validation.ONLY_NUMBERS)
    .max(999999, Validation.MAX_DIGITS_SIX)
    .nullable()
    .transform((value, originalValue) => (originalValue.trim() === '' ? null : value)),
  emailTo: yup
    .string()
    .required(Validation.EMAIL_TO_REQUIRED)
    .test('valid-emails', Validation.THREE_VALID_EMAILS, validateEmails),
  emailCc: yup.string().nullable().test('valid-emails', Validation.THREE_VALID_EMAILS, validateEmails),
  emailSubject: yup
    .string()
    .required(Validation.EMAIL_SUBJECT_REQUIRED)
    .max(100, Validation.MAX_CHAR_ONE_HUNDRED)
    .matches(Regex.REPORTS_ALPHA_NUM_SPACE_SPECIAL, Validation.REPORTS_ALPHA_NUM_SPACE_SPECIAL),
  emailBody: yup
    .string()
    .nullable()
    .max(300, Validation.MAX_CHAR_THREE_HUNDRED)
    .matches(Regex.REPORTS_ALPHA_NUM_SPACE_SPECIAL, Validation.REPORTS_ALPHA_NUM_SPACE_SPECIAL),
  costCentre: yup
    .string()
    .nullable()
    .matches(Regex.ALPHA_NUM_SPACE_HYPHEN, Validation.ALPHA_NUM_SPACE_HYPHEN)
    .max(36, Validation.MAX_CHAR_THIRTY_SIX),
  domesticNumber: yup
    .string()
    .nullable()
    .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
    .max(16, Validation.MAX_CHAR_SIXTEEN),
  registration: yup
    .string()
    .nullable()
    .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
    .max(12, Validation.MAX_CHAR_TWELVE),
  locations: yup.string().nullable().test('locations', Validation.LOCATION_NUMBER, validateLocations),
  monthToDateCardLimit: yup
    .string()
    .nullable()
    .oneOf(['EqualToOrGreaterThan50', 'EqualToOrGreaterThan70', 'EqualToOrGreaterThan80'], ' Month to Date Card Limit')
    .required(Validation.MONTH_TO_DATE_CARD_LIMIT_REQUIRED),
});
